import { useEffect, useId, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { useT } from '../i18n';

/**
 * Themed <select>.
 *
 * Same reason DateField exists: the native option list is browser chrome. CSS
 * reaches the closed control but not the popup it opens, so a styled select
 * still dropped a stock OS list on top of the page. This renders the list too.
 *
 * The value contract is deliberately identical to the native select it
 * replaces — value in, value out, empty string for "nothing chosen" — so every
 * caller's state and query-param handling is untouched.
 */

export interface Option {
  value: string;
  label: string;
}

interface Props {
  value: string;
  onChange: (value: string) => void;
  options: Option[];
  /** Shown when value matches no option — the placeholder equivalent. */
  placeholder?: string;
  /** Labels the button for screen readers when no visible <label> wraps it. */
  ariaLabel?: string;
  disabled?: boolean;
}

/**
 * Does something between the field and the page clip what hangs out of it — a
 * table's horizontal scroller, say? Then an absolutely positioned list is cut
 * off at that box's edge and can only be seen by scrolling the box. A modal
 * card is left out on purpose: inside one the list expands in place (CSS).
 */
function isClipped(el: HTMLElement): boolean {
  if (el.closest('.modal-card')) return false;
  for (let p = el.parentElement; p && p !== document.body; p = p.parentElement) {
    const cs = getComputedStyle(p);
    if (cs.overflowX !== 'visible' || cs.overflowY !== 'visible') return true;
  }
  return false;
}

const GAP = 8;
const MAX_LIST = 264; // matches .select-pop max-height

/** Fixed-position box for the list, below the button or — if there's no room — above it. */
function floatingBox(btn: HTMLElement): CSSProperties {
  const r = btn.getBoundingClientRect();
  const vw = document.documentElement.clientWidth;
  const below = window.innerHeight - r.bottom - GAP * 2;
  const above = r.top - GAP * 2;
  const up = below < Math.min(MAX_LIST, 160) && above > below;
  return {
    position: 'fixed',
    left: Math.max(GAP, Math.min(r.left, vw - r.width - GAP)),
    minWidth: r.width,
    width: 'auto',
    maxHeight: Math.min(MAX_LIST, up ? above : below),
    ...(up ? { bottom: window.innerHeight - r.top + GAP, top: 'auto' } : { top: r.bottom + GAP }),
    zIndex: 85, // over modals (60) and the lightbox (80), under toasts (90)
  };
}

export function Select({ value, onChange, options, placeholder, ariaLabel, disabled }: Props) {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  /** Keyboard cursor. Distinct from `value`: moving it doesn't commit a choice. */
  const [active, setActive] = useState(0);
  const wrap = useRef<HTMLDivElement>(null);
  const btn = useRef<HTMLButtonElement>(null);
  const list = useRef<HTMLDivElement>(null);
  /** Set when the list floats over the page instead of hanging from the field. */
  const [floating, setFloating] = useState<CSSProperties | null>(null);
  const id = useId();

  const selected = options.findIndex((o) => o.value === value);
  const current = selected >= 0 ? options[selected] : undefined;

  const commit = (i: number) => {
    const opt = options[i];
    if (!opt) return;
    onChange(opt.value);
    setOpen(false);
  };

  /** Opening always starts the cursor on what's currently chosen. */
  const show = () => {
    if (disabled) return;
    setActive(selected >= 0 ? selected : 0);
    setFloating(btn.current && isClipped(btn.current) ? floatingBox(btn.current) : null);
    setOpen(true);
  };

  // Escape closes; so does a click anywhere outside the field.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      // The floating list lives under <body>, outside `wrap`, so check it too.
      if (wrap.current?.contains(target) || list.current?.contains(target)) return;
      setOpen(false);
    };
    // A floating list is pinned to where the field was; once the page or the
    // table scrolls under it, it would point at the wrong row — so close it.
    const onMove = (e: Event) => {
      if (list.current?.contains(e.target as Node)) return; // scrolling the list itself
      setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onDown);
    if (floating) {
      window.addEventListener('scroll', onMove, true);
      window.addEventListener('resize', onMove);
    }
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [open, floating]);

  // Keep the cursor in view — long lists (categories) scroll inside the popover.
  useEffect(() => {
    if (!open) return;
    list.current?.querySelector('.select-opt.active')?.scrollIntoView({ block: 'nearest' });
  }, [open, active]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!open) {
      // Match the native control: these keys all open the list.
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        show();
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, options.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Home') {
      e.preventDefault();
      setActive(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      setActive(options.length - 1);
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      commit(active);
    } else if (e.key === 'Tab') {
      setOpen(false); // tabbing away abandons the choice, as the native list does
    }
  };

  // Floating lists go to <body>: a fixed box inside a transformed or filtered
  // ancestor would be positioned against that ancestor, not the screen.
  const renderList = (node: React.ReactNode) => (floating ? createPortal(node, document.body) : node);

  return (
    <div className="selectfield" ref={wrap}>
      <button
        type="button"
        ref={btn}
        className={`selectfield-btn${open ? ' open' : ''}`}
        onClick={() => (open ? setOpen(false) : show())}
        onKeyDown={onKeyDown}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        aria-activedescendant={open ? `${id}-${active}` : undefined}
      >
        <span className={current ? '' : 'ph'}>
          {current ? current.label : (placeholder ?? t('selectPlaceholder'))}
        </span>
        <i className="ri-arrow-down-s-line chev" />
      </button>

      {open && renderList(
        <div
          className="select-pop"
          role="listbox"
          aria-label={ariaLabel}
          ref={list}
          tabIndex={-1}
          style={floating ?? undefined}
        >
          {options.map((o, i) => (
            <button
              type="button"
              key={o.value}
              id={`${id}-${i}`}
              role="option"
              aria-selected={o.value === value}
              className={`select-opt${o.value === value ? ' sel' : ''}${i === active ? ' active' : ''}`}
              // mousedown would fire before the button takes focus and race the
              // outside-click listener; the cursor follows the pointer instead.
              onMouseEnter={() => setActive(i)}
              onClick={() => commit(i)}
            >
              <span>{o.label}</span>
              {o.value === value && <i className="ri-check-line" />}
            </button>
          ))}
        </div>,
      )}
    </div>
  );
}
